import React from 'react';
import {
  MapPinIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  PaperAirplaneIcon,
  TicketIcon,
  MapIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { EXTERNAL_LINKS, LINK_GROUPS } from '../../utils/externalLinks';

interface ExternalLinksSectionProps {
  city: string;
  country: string;
  existingLinks?: {
    tourism_board?: string;
    wikipedia?: string;
    travel_advisory?: string;
  };
}

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  MapPinIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  PaperAirplaneIcon,
  TicketIcon,
  MapIcon,
  ExclamationTriangleIcon,
};

const LinkRow: React.FC<{
  href: string;
  icon: React.ReactNode;
  label: string;
  iconColor?: string;
}> = ({ href, icon, label, iconColor }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
  >
    <span className={iconColor || 'text-cyan-500'}>{icon}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">{label}</span>
    <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
  </a>
);

export const ExternalLinksSection: React.FC<ExternalLinksSectionProps> = ({
  city,
  country,
  existingLinks,
}) => {
  const { t } = useTranslation('destinations');

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 uppercase tracking-wide">
        {t('externalLinks.title')}
      </h3>

      <div className="space-y-4">
        {LINK_GROUPS.map((group) => {
          // Gather links for this group
          const groupLinks = EXTERNAL_LINKS.filter((l) => l.group === group.key);
          const hasExistingLinks =
            group.key === 'general' &&
            (existingLinks?.wikipedia || existingLinks?.tourism_board);
          const hasSafetyLink =
            group.key === 'safety' && existingLinks?.travel_advisory;

          if (groupLinks.length === 0 && !hasExistingLinks && !hasSafetyLink) return null;

          return (
            <div key={group.key}>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                {t(`externalLinks.${group.labelKey}`)}
              </p>
              <div className="space-y-1.5">
                {/* Generated links */}
                {groupLinks.map((link) => {
                  const IconComponent = ICON_MAP[link.icon] || GlobeAltIcon;
                  return (
                    <LinkRow
                      key={link.id}
                      href={link.getUrl(city, country)}
                      icon={<IconComponent className="h-5 w-5" />}
                      label={t(`externalLinks.${link.labelKey}`)}
                      iconColor={link.color}
                    />
                  );
                })}

                {/* Existing curated links in "general" group */}
                {group.key === 'general' && existingLinks?.wikipedia && (
                  <LinkRow
                    href={existingLinks.wikipedia}
                    icon={<GlobeAltIcon className="h-5 w-5" />}
                    label="Wikipedia"
                    iconColor="text-cyan-500"
                  />
                )}
                {group.key === 'general' && existingLinks?.tourism_board && (
                  <LinkRow
                    href={existingLinks.tourism_board}
                    icon={<GlobeAltIcon className="h-5 w-5" />}
                    label={t('profile.tourismBoard')}
                    iconColor="text-cyan-500"
                  />
                )}

                {/* Travel advisory in "safety" group */}
                {group.key === 'safety' && existingLinks?.travel_advisory && (
                  <LinkRow
                    href={existingLinks.travel_advisory}
                    icon={<ExclamationTriangleIcon className="h-5 w-5" />}
                    label={t('profile.travelAdvisory')}
                    iconColor="text-amber-500"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
